import { useContext, useEffect, useState } from 'react';
import keyBy from 'lodash/keyBy';
import { AppConstantsContext } from '../../../context';
import { AsyncCheckoutService } from '../../../services';
// Adjust path as needed

export interface PriceData {
  oldPrice: string | null;
  price: string;
  productId: string | number;
  isYearly: boolean;
  isRecurring: boolean;
  hasDiscount: boolean;
}

export interface PriceDataMap {
  [key: string]: PriceData;
}

const matchPriceString = (str: string) => {
  const tmpPrice = str.replace(',', '');
  // regex matches 0-3 possible leading characters in first group
  // possible currency symbol in the second group
  // and the amount in the last group
  const regex =
    /^([A-Z]{0,3})?\s?(?:\u00A0)?([$\u00A2-\u00A5\xA2-\u00A3\xA5\u058F\u060B\u09F2\u09F3\u09FB\u0AF1\u0BF9\u0E3F\u17DB\u20A0-\u20CF\uA838\uFDFC\uFE69\uFF04\uFFE0\uFFE1\uFFE5\uFFE6])?\s?(?:\u00A0)?([\d,]+(?:\.\d{1,2})?)\s*$/u;
  return tmpPrice.match(regex);
};

export const getCurrency = (formattedPrice: string) => {
  const val = matchPriceString(formattedPrice);
  if (val) return val[2] || val[1];
  return formattedPrice;
};
export const getAmount = (formattedPrice: string) => {
  const matches = matchPriceString(formattedPrice);
  if (!matches) return formattedPrice;
  return matches[3];
};

export const getPricePerMonth = (formattedPrice: string, isYearly: boolean) => {
  if (isYearly) {
    const currency = getCurrency(formattedPrice);
    const amount = getAmount(formattedPrice);
    const yearlyPrice = parseFloat(amount) / 12;
    return `${currency}${yearlyPrice.toFixed(2)}`;
  }
  return formattedPrice;
};

export const getDiscountedPrice = (formattedPrice: string, discount: number = 0) => {
  const currency = getCurrency(formattedPrice);
  const amount = getAmount(formattedPrice);

  const originalPrice = parseFloat(amount);
  const discountedPrice = originalPrice * (1 - discount / 100);

  return `${currency}${discountedPrice.toFixed(2)}`;
};

export const usePricing = (discount: number = 0, discountedSubscriptions: string[] = []) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { subscriptionIds } = useContext(AppConstantsContext);
  const [priceData, setPriceData] = useState<PriceDataMap>(null);

  const fetchPrice = async () => {
    setLoading(true);
    try {
      const filteredDiscountedSubscriptionIds = Object.values(subscriptionIds).filter(
        (subscriptionId: string) => discountedSubscriptions.includes(subscriptionId)
      );

      const pricesArray = await Promise.all(
        Object.values(subscriptionIds).map(async (subscriptionId: string) => {
          const p = await AsyncCheckoutService.getPriceInfo(subscriptionId);
          const isDiscountedPrice = filteredDiscountedSubscriptionIds.includes(subscriptionId);

          const isYearly = p.recurring?.subscription?.type === 'year';

          let price = p.price.gross;
          if (isDiscountedPrice) {
            price = getDiscountedPrice(p.price.gross, discount);
          }
          return {
            productId: p.productId,
            oldPrice: isDiscountedPrice ? getPricePerMonth(p.price.gross, isYearly) : null,
            price: getPricePerMonth(price, isYearly),
            isYearly,
            isRecurring: !!p.recurring,
            hasDiscount: isDiscountedPrice,
          };
        })
      );
      const prices = keyBy(pricesArray, 'productId');
      setPriceData(prices);
    } catch (e) {
      console.error('Error fetching prices:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrice();
  }, []);

  return { priceData, loading, error };
};

export default usePricing;
